import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, X } from 'lucide-react';
import { formatDateLocale } from '../utils/dateUtilsLocale';

interface ShutdownNoticeProps {
  className?: string;
}

const ShutdownNotice: React.FC<ShutdownNoticeProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine date text based on current date
  const getFinalDeliveryDateText = () => {
    const now = new Date();
    const cutoffDate = new Date(2025, 7, 1); // August 1, 2025 (month is 0-indexed)
    const finalDeliveryDate = new Date(2025, 6, 27); // July 27, 2025 (weekend)

    if (now < cutoffDate) {
      // Before August 1st, show "this weekend"
      return t('common.shutdown.thisWeekend');
    } else {
      // After August 1st, show specific date
      const formattedDate = formatDateLocale(finalDeliveryDate, 'PPP');
      return t('common.shutdown.specificDate', { date: formattedDate });
    }
  };

  return (
    <>
      {/* Clickable Header */}
      <div className={`bg-blue-50 border border-blue-200 rounded-xl mx-4 my-4 ${className}`}>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full p-4 flex items-center space-x-3 text-left hover:bg-blue-100 rounded-xl transition-colors"
        >
          <Heart className="h-5 w-5 text-blue-600" />
          <h3 className="text-base font-semibold text-blue-900">
            {t('common.shutdown.title')}
          </h3>
        </button>
      </div>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setIsModalOpen(false)}
        >
          {/* Letter-style Modal */}
          <div 
            className="relative bg-white max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, #fefefe 0%, #f8f9fa 100%)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed Letter Header */}
            <div className="relative flex-shrink-0 p-6 pb-4 border-b border-gray-200">
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
              
              {/* Letter Title & Date */}
              <div className="pr-12">
                <div className="flex items-center space-x-3 mb-2">
                  <Heart className="h-6 w-6 text-red-500" />
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">
                    {t('common.shutdown.title')}
                  </h2>
                </div>
                {/* Letter Date */}
                <div className="text-right text-sm text-gray-500 font-medium mt-2">
                  {formatDateLocale(new Date(), 'PPP')}
                </div>
              </div>
            </div>

            {/* Scrollable Letter Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 pt-6">
                <div className="space-y-6 text-gray-800 leading-relaxed">
                  {/* Letter Body */}
                  <p className="text-base">
                    {t('common.shutdown.description')}
                  </p>
                  
                  <p className="text-base">
                    {t('common.shutdown.gratitude')}
                  </p>
                  
                  <p className="text-base font-medium">
                    {t('common.shutdown.finalDeliveries', { dateText: getFinalDeliveryDateText() })}
                  </p>
                  
                  <p className="text-base font-medium">
                    {t('common.shutdown.refunds')}
                  </p>
                  
                  <p className="text-base font-medium">
                    {t('common.shutdown.thankYou')}
                  </p>
                  
                  <div className="text-right mt-6">
                    <p className="text-base font-semibold text-gray-900">
                      {t('common.shutdown.signature')}
                    </p>
                  </div>
                </div>
                
                {/* Letter Bottom Padding */}
                <div className="h-6"></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShutdownNotice;